import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactDOMServer from 'react-dom/server';
import { Button } from 'reactstrap';
import { Model, SvgRegistry } from 'survey-core';
import { Survey } from 'survey-react-ui';
import { toast } from 'react-toastify';
import useConfig from '../../../util/useConfig';
import Api from '../../../util/api';
import ReservationOptionEditor, {
  createOption,
} from './QuestionnaireBuilder/ReservationOptionEditor';
import { EventContext } from '../context';

import { DragHandleSVGIcon } from './QuestionnaireBuilder/DragHandle';
import s, { partial } from '../../../util/translation/translation';

const DRAG_ICON = ReactDOMServer.renderToString(<DragHandleSVGIcon />);

const questionTypeElement = {
  type: 'dropdown',
  name: 'question_type',
  title: s('question.type'),
  titleLocation: 'top',
  isRequired: true,
  choices: [
    {
      value: 'text_field',
      text: s('question.textInput'),
    },
    {
      value: 'radio_button',
      text: s('question.radioGroup'),
    },
    {
      value: 'checkbox',
      text: s('question.checkbox'),
    },
    {
      value: 'dropdown',
      text: s('question.dropdown'),
    },
    {
      value: 'reservation',
      text: s('question.reservation'),
    },
  ],
  placeholder: s('question.selectType'),
};

const surveyJson = {
  logoPosition: 'right',
  pages: [
    {
      name: 'page1',
      elements: [
        {
          type: 'paneldynamic',
          name: 'question',
          titleLocation: 'hidden',
          templateElements: [
            {
              type: 'text',
              name: 'question',
              title: s('question.question'),
              titleLocation: 'top',
              isRequired: true,
              placeholder: s('question.enterQuestion'),
            },
            questionTypeElement,
            {
              type: 'matrixdynamic',
              name: 'choices',
              visibleIf:
                "{panel.question_type} = 'radio_button' or {panel.question_type} = 'checkbox' or {question[0].question_type} = 'dropdown'",
              title: s('question.options'),
              titleLocation: 'top',
              isRequired: true,
              showHeader: false,
              columns: [
                {
                  name: 'choice',
                  title: s('question.choice'),
                },
              ],
              choices: [1, 2, 3, 4, 5],
              placeholder: s('question.optionPlaceholder'),
              cellType: 'text',
              rowCount: 1,
              addRowText: s('question.reservationOptionAdd'),
              allowRowsDragAndDrop: true,
            },
          ],
          allowAddPanel: false,
          allowRemovePanel: false,
          panelCount: 1,
          panelAddText: s('question.addNewQuestion'),
        },
      ],
    },
  ],
  showTitle: false,
  widthMode: 'responsive',
};

SvgRegistry.registerIconFromSvg('v2dragelement_16x16', DRAG_ICON);

const FormBuilder = ({ setShowModal, editableQuestion }) => {
  const isEditMode = !!editableQuestion;

  const qT = partial('question');

  if (isEditMode) {
    surveyJson.completeText = qT('edit');
  } else {
    surveyJson.completeText = qT('add');
  }
  // The question type determines which fields/validation apply, so it
  // can't be changed once a question already exists.
  questionTypeElement.readOnly = isEditMode;

  // Persist survey model across renders so React state updates don't recreate it
  const surveyRef = useRef(null);
  if (!surveyRef.current) {
    surveyRef.current = new Model(surveyJson);
  }
  const survey = surveyRef.current;

  const [reservationOptions, setReservationOptions] = useState([]);
  // Keep a ref so the onComplete callback (set up once) can read the latest options
  const reservationOptionsRef = useRef(reservationOptions);
  reservationOptionsRef.current = reservationOptions;

  const [currentQuestionType, setCurrentQuestionType] = useState(
    editableQuestion?.question?.question_type || null
  );
  const isReservation = currentQuestionType === 'reservation';

  // Track the live question_type so we can show the reservation options
  // editor (and swap out the survey's own submit button for our own, placed
  // after the options) only while that type is selected.
  const onValueChangedSetupRef = useRef(false);
  if (!onValueChangedSetupRef.current) {
    onValueChangedSetupRef.current = true;
    survey.onValueChanged.add((sender) => {
      setCurrentQuestionType(sender.data?.question?.[0]?.question_type || null);
    });
  }

  useEffect(() => {
    survey.showNavigationButtons = isReservation ? 'none' : 'bottom';
  }, [isReservation, survey]);

  // A reservation question is meaningless with zero options, same as how
  // the choices matrixdynamic for checkbox/radio/dropdown always starts
  // with one row (rowCount: 1) - seed one here too instead of leaving the
  // admin looking at an empty list with nothing to fill in yet.
  useEffect(() => {
    if (isReservation && reservationOptions.length === 0) {
      setReservationOptions([createOption()]);
    }
  }, [isReservation, reservationOptions.length]);

  const { id } = useConfig();
  const {
    questions,
    pickedQuestions,
    setPickedQuestions,
    eventId,
    fetchQuestions,
    pickedQuestionId,
  } = useContext(EventContext);

  const createSharedQuestion = useCallback(async (sender) => {
    try {
      const foundSharedQuestion = questions.find(
        (q) =>
          (q.question?.question).toLowerCase() ===
          sender.data.question[0].question.toLowerCase()
      );
      if (foundSharedQuestion) {
        toast.error(qT('simillarQuestionsAvailableInLibrary'));
      } else {
        await Api.questions.createSharedQuestion(
          {
            domain: 'event',
            question: sender.data.question[0],
            domainId: eventId,
          },
          id
        );
        const { data } = await Api.questions.fetchDomainSpecificQuestions(
          eventId,
          id
        );
        setPickedQuestions(data.question);
        fetchQuestions(true);
        toast.success(qT('questionsAdded'));
      }
    } catch (error) {
      toast.error(error);
    }
  }, []);

  const updateQuestions = useCallback(
    async (sender, questionType = 'shared') => {
      const updateQuestion = sender.data.question[0];
      try {
        if (questionType === 'domain') {
          const currentPickedQuestions = [...pickedQuestions];
          const index = currentPickedQuestions.findIndex(
            (question) => question.id === editableQuestion.id
          );
          if (index !== -1) {
            currentPickedQuestions[index] = updateQuestion;
          }
          await Api.questions.updateQuestion(
            pickedQuestionId,
            {
              type: questionType,
              question: currentPickedQuestions,
            },
            id
          );
          const { data } = await Api.questions.fetchDomainSpecificQuestions(
            eventId,
            id
          );
          setPickedQuestions(data.question);
        } else {
          await Api.questions.updateQuestion(
            editableQuestion.id,
            {
              type: questionType,
              question: updateQuestion,
              domain: 'event',
            },
            id
          );
          fetchQuestions(true);
        }
        toast.success(qT('questionsUpdated'));
      } catch (error) {
        toast.error(error);
      }
    },
    []
  );

  // Set up onComplete once (survey persists across renders via useRef)
  const onCompleteSetupRef = useRef(false);
  if (!onCompleteSetupRef.current) {
    onCompleteSetupRef.current = true;
    survey.onComplete.add(async (sender) => {
      setShowModal();
      const questionData = sender.data.question[0];

      if (questionData.question_type === 'reservation') {
        questionData.options = reservationOptionsRef.current;
        delete questionData.choices;
      }

      const wrappedSender = { data: { question: [questionData] } };

      if (isEditMode) {
        updateQuestions(wrappedSender, editableQuestion?.type);
      } else {
        createSharedQuestion(wrappedSender);
      }
    });
  }

  if (isEditMode) {
    survey.data = { question: [editableQuestion.question] };
    if (editableQuestion.question.question_type === 'reservation') {
      // Only pre-fill once — check if options are already set
      if (reservationOptions.length === 0) {
        setReservationOptions(editableQuestion.question.options || []);
      }
    }
  }

  return (
    <div className="event-question-survey">
      <Survey model={survey} />
      {isReservation && (
        <div className="reservation-options-card sd-body sd-body--responsive">
          <ReservationOptionEditor
            options={reservationOptions}
            onChange={setReservationOptions}
          />
          <Button
            color="primary"
            type="button"
            onClick={() => survey.completeLastPage()}
          >
            {isEditMode ? qT('edit') : qT('add')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FormBuilder;
